import * as CookieConsent from 'vanilla-cookieconsent';
import mapCookieCategoriesToGaConsent from '../mapCookieCategoriesToGaConsent';
import normalizeConsentParams from '~/shared/utils/normalizeConsentParams';
import type { ConsentParams } from '~/shared/types';
import safeLocalStorage from '~/utils/safeLocalStorage';

type Args = {
  cookie: CookieConsent.CookieValue;
  storageKey: string;
  setConsentState: (consent: ConsentParams) => void;
  setHasStoredConsent?: (value: boolean) => void;
};

/**
 * Read the user's cookie choices and apply them.
 * - Get accepted categories from CookieBanner.
 * - Convert them to the 4 or 6 Google Analytics consent keys.
 * - Make sure all keys are present, save to localStorage, update state.
 */
const persistAndEmitConsentFromCookie = (args: Args): void => {
  const { cookie, storageKey, setConsentState, setHasStoredConsent } = args;

  /** E.g: ['necessary', 'analytics', 'marketing', 'functional'] */
  const acceptedCategories: Array<string> =
    cookie.categories && cookie.categories.length > 0
      ? cookie.categories
      : (CookieConsent.getUserPreferences()?.acceptedCategories ?? []);

  /**
   * Maps cookie categories to Google Consent Mode keys, then serializes safely to 4 or 6 keys.
   * Example output:
   * {
   *   "ad_storage": "denied" | "granted",
   *   "ad_user_data": "denied" | "granted",
   *   "ad_personalization": "denied" | "granted",
   *   "analytics_storage": "denied" | "granted",
   * }
   */
  const consent = normalizeConsentParams(
    mapCookieCategoriesToGaConsent(acceptedCategories),
  );

  safeLocalStorage.setItem(storageKey, JSON.stringify(consent));
  setConsentState(consent);

  if (setHasStoredConsent) setHasStoredConsent(true);
};

export default persistAndEmitConsentFromCookie;
